import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiArrowRight, FiZap } = FiIcons;

function CallToAction() {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-r from-orange-400/5 to-red-400/5 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-orange-500/10 backdrop-blur-sm border border-orange-500/20 px-6 py-2 rounded-full text-orange-400 font-semibold mb-6">
            <SafeIcon icon={FiZap} className="w-4 h-4" />
            <span>Ready to Transform Your Sales?</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">
            Ready to get the perks of
            <br />
            a <span className="text-orange-400 font-bold">24/7 sales team</span>
            <br />
            <span className="text-orange-400 font-bold">WITHOUT</span> the expense?
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Make more sales & get better qualified leads
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Find out if you can remove expensive bottlenecks and add a few extra zeros to
              your business account with AI. Book in a call here, so we can chat.
            </p>
            
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                <span>Schedule a Call</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
            </Link>
          </motion.div>

          {/* Right Features */}
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={FiZap} className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-white">Instant Results</h4>
              </div>
              <p className="text-gray-300 leading-relaxed">
                See immediate improvements in lead response times and conversion rates
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-white">No Setup Hassle</h4>
              </div>
              <p className="text-gray-300 leading-relaxed">
                We handle everything from implementation to ongoing optimization
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={FiCalendar} className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-white">Free Consultation</h4>
              </div>
              <p className="text-gray-300 leading-relaxed">
                Get expert analysis of your sales process at no cost
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default CallToAction;